(() => {
  'use strict';

  const STORAGE_KEY = 'flatoo_recent_searches';
  const configElement = document.getElementById('FlatooSearchConfig');
  let config = {
    searchUrl: '/search',
    placeholder: '商品名・サイズ・用途で検索',
    recentEnabled: true,
    recentLimit: 5,
    ga4Enabled: true
  };

  if (configElement) {
    try {
      config = { ...config, ...JSON.parse(configElement.textContent) };
    } catch (error) {
      console.warn('[flatoo search] Failed to parse search config.', error);
    }
  }

  const normalizeTerm = (value) =>
    String(value || '')
      .replace(/\*+$/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const safeJsonParse = (value, fallback) => {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch (_) {
      return fallback;
    }
  };

  const getRecentSearches = () => {
    if (!config.recentEnabled) return [];

    try {
      return safeJsonParse(localStorage.getItem(STORAGE_KEY), [])
        .map(normalizeTerm)
        .filter(Boolean)
        .slice(0, Number(config.recentLimit) || 5);
    } catch (_) {
      return [];
    }
  };

  const saveRecentSearch = (term) => {
    if (!config.recentEnabled) return;

    const normalized = normalizeTerm(term);
    if (!normalized) return;

    try {
      const searches = getRecentSearches().filter(
        (item) => item.toLocaleLowerCase() !== normalized.toLocaleLowerCase()
      );
      searches.unshift(normalized);
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(searches.slice(0, Number(config.recentLimit) || 5))
      );
    } catch (_) {
      // localStorage can be unavailable in privacy modes.
    }
  };

  const clearRecentSearches = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {
      // Ignore storage errors.
    }
    renderRecentSearches();
  };

  const buildSearchUrl = (term) => {
    const url = new URL(config.searchUrl || '/search', window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return `${url.pathname}${url.search}`;
  };

  const renderRecentSearches = () => {
    document.querySelectorAll('[data-flatoo-recent-searches]').forEach((block) => {
      const list = block.querySelector('[data-flatoo-recent-list]');
      if (!list) return;

      const limit = Number(block.dataset.recentLimit || config.recentLimit || 5);
      const recentSearches = getRecentSearches().slice(0, limit);

      list.innerHTML = '';

      if (!recentSearches.length) {
        block.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();

      recentSearches.forEach((term) => {
        const item = document.createElement('li');
        item.className = 'item';

        const link = document.createElement('a');
        link.className = 'link';
        link.href = buildSearchUrl(term);
        link.dataset.recentSearch = '';
        link.dataset.searchTerm = term;
        link.innerHTML = `
          <span aria-hidden="true" style="font-size:16px;line-height:1">↺</span>
          <span class="text"></span>
        `;
        link.querySelector('.text').textContent = term;

        item.appendChild(link);
        fragment.appendChild(item);
      });

      list.appendChild(fragment);
      block.hidden = false;
    });
  };

  const setSearchPlaceholders = () => {
    if (!config.placeholder) return;

    document.querySelectorAll('input[type="search"][name="q"]').forEach((input) => {
      input.placeholder = config.placeholder;
      input.setAttribute('aria-label', config.placeholder);
    });
  };

  const sendAnalyticsEvent = (eventName, params = {}) => {
    if (!config.ga4Enabled) return;

    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== '')
    );

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...cleanParams
    });
  };

  const eventData = (element) => ({
    search_term: normalizeTerm(element?.dataset.searchTerm),
    item_id: element?.dataset.productId,
    item_name: element?.dataset.productTitle,
    collection_id: element?.dataset.collectionId,
    collection_name: element?.dataset.collectionTitle,
    page_location: window.location.href
  });

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest('form[role="search"], form.search-form');
      if (!form) return;

      const input = form.querySelector('input[name="q"]');
      const term = normalizeTerm(input?.value);
      if (!term) return;

      saveRecentSearch(term);
      renderRecentSearches();
      sendAnalyticsEvent('search_submit', {
        search_term: term,
        page_location: window.location.href
      });
    },
    true
  );

  document.addEventListener('click', (event) => {
    const clearButton = event.target.closest('[data-clear-recent-searches]');
    if (clearButton) {
      event.preventDefault();
      clearRecentSearches();
      return;
    }

    const trackedElement = event.target.closest(
      [
        '[data-search-suggestion]',
        '[data-predictive-product]',
        '[data-no-result-product]',
        '[data-predictive-collection]',
        '[data-predictive-view-all]',
        '[data-recent-search]',
        '[data-popular-search]',
        '[data-search-shortcut]'
      ].join(',')
    );

    if (!trackedElement) return;

    const term = normalizeTerm(trackedElement.dataset.searchTerm);
    if (term) saveRecentSearch(term);

    let eventName = 'search_suggestion_click';
    if (trackedElement.matches('[data-predictive-product]')) {
      eventName = 'predictive_search_product_click';
    } else if (trackedElement.matches('[data-no-result-product]')) {
      eventName = 'search_no_results_product_click';
    } else if (trackedElement.matches('[data-predictive-collection]')) {
      eventName = 'predictive_search_collection_click';
    } else if (trackedElement.matches('[data-predictive-view-all]')) {
      eventName = 'predictive_search_view_all';
    } else if (trackedElement.matches('[data-recent-search]')) {
      eventName = 'recent_search_click';
    } else if (trackedElement.matches('[data-popular-search]')) {
      eventName = 'popular_search_click';
    } else if (trackedElement.matches('[data-search-shortcut]')) {
      eventName = 'search_shortcut_click';
    }

    sendAnalyticsEvent(eventName, eventData(trackedElement));
  });

  const trackNoResults = () => {
    document
      .querySelectorAll('[data-flatoo-no-results], [data-predictive-no-results]')
      .forEach((element) => {
        if (element.dataset.analyticsTracked === 'true') return;
        element.dataset.analyticsTracked = 'true';

        const term = normalizeTerm(element.dataset.searchTerm);
        const eventName = element.matches('[data-predictive-no-results]')
          ? 'predictive_search_no_results'
          : 'search_no_results';

        sendAnalyticsEvent(eventName, {
          search_term: term,
          page_location: window.location.href
        });
      });
  };

  document.addEventListener('flatoo:predictive-search-rendered', trackNoResults);
  document.addEventListener('shopify:section:load', () => {
    setSearchPlaceholders();
    renderRecentSearches();
    trackNoResults();
  });

  const queryTerm = normalizeTerm(
    new URLSearchParams(window.location.search).get('q')
  );
  if (window.location.pathname.includes('/search') && queryTerm) {
    saveRecentSearch(queryTerm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setSearchPlaceholders();
      renderRecentSearches();
      trackNoResults();
    });
  } else {
    setSearchPlaceholders();
    renderRecentSearches();
    trackNoResults();
  }
})();
